import { call, put, takeEvery } from "redux-saga/effects"
import { IResponse, Route } from "../../types/global/types"
import type { SagaIterator } from 'redux-saga'
import {
  BulkDeleteRoutes,
  CreateRoute,
  DeleteRoute,
  UpdateRoute,
} from "../../types/sagas"

import {
  GetRoutesTriggerAction,
  GetRouteTriggerAction,
} from "../../types/reducers/routesReducer"
import { updateToken } from "../../utils/updateToken"
import { routesAPI } from "../api/api"
import { generalActions } from "../store/reducers/generalReducer"
import { isOnline } from "../../offline/isOnline"
import { isOfflineFeatureEnabled } from "../../offline/featureFlag"
import { enqueue } from "../../offline/queue"
import { newIdempotencyKey } from "../../offline/idempotency"
import { publishCounters } from "../../offline/counters"
import { ROUTE_DETAIL_PARAMS } from "../../offline/routeParams"
import i18n from "../../res/languages"
import {
  actions,
  actionTypes,
  routesTriggerActions,
  PendingRoutePayload,
} from "../store/reducers/routesReducer"

// Keep track of in-flight getRoutes requests to avoid duplicate parallel calls
const inFlightGetRoutes = new Set<string>()

function* getRoutes(action: GetRoutesTriggerAction): SagaIterator {
  // Action properties are spread directly, not in a payload property
  const { household, address, page, perPage, searchValue } = action as any

  const normalizedPayload = {
    household,
    address,
    page,
    perPage,
    searchValue: searchValue || null,
  }

  const key = JSON.stringify(normalizedPayload)

  if (inFlightGetRoutes.has(key)) {
    return
  }

  inFlightGetRoutes.add(key)

  try {
    const response: IResponse<Route> = yield call(
      routesAPI.getRoutes,
      normalizedPayload
    )

    if (response.status === 200) {
      updateToken()
      yield put(actions.getRoutesAC(response.data))
    }
  } catch (error) {
    console.error('[routesSaga] getRoutes ERROR:', error)
  } finally {
    inFlightGetRoutes.delete(key)
  }
}


export function* getRoute(action: GetRouteTriggerAction): SagaIterator {
  // Action properties are spread directly, not in a payload property
  const { id, household, address } = action as any

  try {
    if (id) {
      const response: IResponse<Route> = yield call(
        routesAPI.getRoute,
        { id, household, address }
      )

      if (response.status === 200) {
        updateToken()
        yield put(actions.getRouteAC(response.data.data))
      }
    } else {
      yield put(actions.getRouteAC(null))
    }
  } catch (error) {
    console.error(error)
  }
}



export function* createRoute(payload: CreateRoute & { draftId?: string }) {
  try {
    const response: { status: number; data: {} } = yield routesAPI.createRoute(
      payload.data
    )
    if (response.status === 201) {
      updateToken()
      yield put(
        generalActions.successAC({ message: i18n.t("general.created"), type: "success" })
      )
    }
  } catch (error: any) {
    if (error.response?.status === 409 && error.response?.data?.conflict) {
      // The conflict dialog is about to ask the user what to do — the draft stays
      // stashed (draftId travels in the pending payload) until that flow definitively
      // ends without the route being saved: Cancel or a failed retry. Restoring it here,
      // at conflict-detection time, would leave a phantom copy behind if the user
      // resolves the conflict successfully.
      yield put(
        actions.showMoveConflictAC(
          { ...error.response.data.conflict, conflicts: error.response.data.conflicts },
          { mode: "create", data: payload.data, draftId: payload.draftId }
        )
      )
    } else {
      // The map panel removed this draft optimistically before dispatching, so a
      // non-conflict failure has to hand it back — otherwise the route is just gone.
      yield put(actions.createRouteFailedAC(payload.draftId))
      yield put(
        generalActions.errorAC({
          message: error.response?.data?.message || i18n.t("general.an_error_occurred"),
          type: "error",
        })
      )
    }
  }
}

function* updateRoute(payload: UpdateRoute) {
  try {
    const response: { status: number } = yield routesAPI.updateRoute({
      ...payload,
    })
    if (response.status === 200) {
      console.log(response)
      updateToken()
      yield put(
        routesTriggerActions.getRouteTriggerAC({
          id: payload.id,
          ...ROUTE_DETAIL_PARAMS,
        })
      )
      yield put(
        generalActions.successAC({ message: i18n.t("general.updated"), type: "success" })
      )
    }
  } catch (error: any) {
    if (error.response?.status === 409 && error.response?.data?.conflict) {
      yield put(
        actions.showMoveConflictAC(
          { ...error.response.data.conflict, conflicts: error.response.data.conflicts },
          { mode: "update", id: payload.id, data: payload.data }
        )
      )
    } else {
      yield put(
        generalActions.errorAC({
          message: error.response?.data?.message || i18n.t("general.an_error_occurred"),
          type: "error",
        })
      )
    }
  }
}

/**
 * Retry the write the conflict dialog was raised for, with one override flag.
 *
 * force_move → the contact is removed from every conflicting route.
 * force_add  → the contact ends up on all of them.
 */
function* retryWithFlag(
  payload: PendingRoutePayload,
  flag: "force_move" | "force_add"
): SagaIterator {
  try {
    if (payload.mode === "create") {
      const response: { status: number } = yield call(routesAPI.createRoute, {
        ...payload.data,
        [flag]: true,
      } as any)
      // Every outcome must leave the dialog closed. A non-201 that does not throw used to
      // fall through silently, stranding the dialog open with no way forward but Cancel and
      // never handing the drawn route back.
      yield put(actions.clearMoveConflictAC())

      if (response.status === 201) {
        updateToken()
        yield put(actions.clearFailedDraftAC())
        yield put(
          generalActions.successAC({ message: i18n.t("general.created"), type: "success" })
        )
      } else {
        // The route was not created, so treat it like any other failed create: hand the
        // draft back and say so.
        yield put(actions.createRouteFailedAC(payload.draftId))
        yield put(
          generalActions.errorAC({
            message: i18n.t("general.an_error_occurred"),
            type: "error",
          })
        )
      }
      return
    }

    const response: { status: number } = yield call(routesAPI.updateRoute, {
      id: payload.id as number,
      data: { ...payload.data, [flag]: true },
    })
    if (response.status === 200) {
      updateToken()
      yield put(actions.clearMoveConflictAC())
      yield put(
        routesTriggerActions.getRouteTriggerAC({
          id: payload.id ?? null,
          ...ROUTE_DETAIL_PARAMS,
        })
      )
      yield put(
        generalActions.successAC({ message: i18n.t("general.updated"), type: "success" })
      )
    }
  } catch (error: any) {
    yield put(actions.clearMoveConflictAC())
    if (payload.mode === "create") {
      // The retry failed too — the route still was not saved, so hand the draft back
      // the same way the initial non-conflict failure does.
      yield put(actions.createRouteFailedAC(payload.draftId))
    }
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || i18n.t("general.an_error_occurred"),
        type: "error",
      })
    )
  }
}

function* forceMovePoint(action: { type: string; payload: PendingRoutePayload }): SagaIterator {
  // target is es5 without downlevelIteration, so `yield*` delegation to a generator
  // doesn't typecheck here — `call` is redux-saga's own delegation effect and is
  // functionally equivalent.
  yield call(retryWithFlag, action.payload, "force_move")
}

export function* forceAddPoint(action: { type: string; payload: PendingRoutePayload }): SagaIterator {
  yield call(retryWithFlag, action.payload, "force_add")
}

function* deleteRoute(payload: DeleteRoute) {
  try {
    const response: IResponse<Route> = yield routesAPI.deleteRoute(payload.id)
    if (response.status === 200) {
      updateToken()
      yield put(actions.getRoutesAC({ data: null }))
      yield put(
        routesTriggerActions.getRoutesTriggerAC({
          household: true,
          address: true,
        })
      )
      yield put(
        generalActions.successAC({ message: i18n.t("general.deleted"), type: "success" })
      )
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || i18n.t("general.an_error_occurred"),
        type: "error",
      })
    )
  }
}

function* bulkDeleteRoutes(payload: BulkDeleteRoutes) {
  try {
    const response: IResponse<Route> = yield routesAPI.bulkDeleteRoutes(payload.ids)
    if (response.status === 200) {
      updateToken()
      yield put(actions.getRoutesAC({ data: null }))
      yield put(
        routesTriggerActions.getRoutesTriggerAC({
          household: true,
          address: true,
        })
      )
      yield put(
        generalActions.successAC({ message: i18n.t("general.deleted"), type: "success" })
      )
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || i18n.t("general.an_error_occurred"),
        type: "error",
      })
    )
  }
}

function* deleteAllRoutes() {
  try {
    const response: IResponse<Route> = yield call(routesAPI.deleteAllRoutes)
    if (response.status === 200) {
      updateToken()
      yield put(actions.getRoutesAC({ data: null }))
      yield put(
        routesTriggerActions.getRoutesTriggerAC({
          household: true,
          address: true,
        })
      )
      yield put(
        generalActions.successAC({ message: i18n.t("general.deleted"), type: "success" })
      )
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error?.response?.data?.message || i18n.t("general.an_error_occurred"),
        type: "error",
      })
    )
  }
}

/**
 * Recomputes the offline counters from the queue and pushes them into the store.
 * Lives here for now because it is the only consumer; the sync engine takes it
 * over in the task that introduces the flush loop.
 */
export function* refreshOfflineCounters(): SagaIterator {
  // publishCounters is the single implementation of the counting logic (it also runs
  // after every sync flush). It takes a plain dispatch, so here its output is
  // collected and re-yielded as puts, keeping the saga testable with runSaga.
  const collected: unknown[] = [];
  yield call(publishCounters, (action: unknown) => {
    collected.push(action);
  });
  for (const action of collected) {
    yield put(action as any);
  }
}

export function* markPointVisited(action: any): SagaIterator {
  const { routeId, pointId } = action;

  if (!isOnline() && isOfflineFeatureEnabled()) {
    // Offline: the reducer has already applied the optimistic visited_at. Persist the
    // intent and let the sync engine replay it — deliberately no FAILED dispatch, which
    // would roll the UI back and lose the volunteer's tap.
    yield call(enqueue, {
      type: "MARK_POINT_VISITED",
      entityKey: `point:${pointId}`,
      payload: { routeId, pointId },
      idempotencyKey: newIdempotencyKey(),
    });
    yield call(refreshOfflineCounters);
    return;
  }

  try {
    const response: any = yield call(routesAPI.markPointVisited, routeId, pointId);
    if (response.status === 200) {
      const point = response.data.data;
      yield put(actions.markPointVisitedSuccessAC(pointId, point.visited_at, point.visited_by_user_id));
    }
  } catch (error: any) {
    yield put(actions.markPointVisitedFailedAC(pointId));
    yield put(generalActions.errorAC({ message: error.response?.data?.message || "Error", type: "error" }));
  }
}

export function* unmarkPointVisited(action: any): SagaIterator {
  const { routeId, pointId } = action;

  if (!isOnline() && isOfflineFeatureEnabled()) {
    yield call(enqueue, {
      type: "UNMARK_POINT_VISITED",
      entityKey: `point:${pointId}`,
      payload: { routeId, pointId },
      idempotencyKey: newIdempotencyKey(),
    });
    yield call(refreshOfflineCounters);
    return;
  }

  try {
    const response: any = yield call(routesAPI.unmarkPointVisited, routeId, pointId);
    if (response.status === 200) {
      yield put(actions.unmarkPointVisitedSuccessAC(pointId));
    }
  } catch (error: any) {
    yield put(actions.unmarkPointVisitedFailedAC(pointId));
    yield put(generalActions.errorAC({ message: error.response?.data?.message || "Error", type: "error" }));
  }
}

export function* routesWatcher() {
  yield takeEvery(actionTypes.GET_ROUTES_TRIGGER, getRoutes)
  yield takeEvery(actionTypes.GET_ROUTE_TRIGGER, getRoute)
  yield takeEvery(actionTypes.CREATE_ROUTE_TRIGGER, createRoute)
  yield takeEvery(actionTypes.UPDATE_ROUTE_TRIGGER, updateRoute)
  yield takeEvery(actionTypes.DELETE_ROUTE_TRIGGER, deleteRoute)
  yield takeEvery(actionTypes.BULK_DELETE_ROUTES_TRIGGER, bulkDeleteRoutes)
  yield takeEvery(actionTypes.DELETE_ALL_ROUTES_TRIGGER, deleteAllRoutes)
  yield takeEvery(actionTypes.MARK_POINT_VISITED, markPointVisited)
  yield takeEvery(actionTypes.UNMARK_POINT_VISITED, unmarkPointVisited)
  yield takeEvery(actionTypes.FORCE_MOVE_POINT_TRIGGER, forceMovePoint)
  yield takeEvery(actionTypes.FORCE_ADD_POINT_TRIGGER, forceAddPoint)
}
